import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import storage from '@/utils/storage';
import { useScreenSize } from '@/hooks';
import {
  fetchReferralInfo,
  fetchTeamMembers,
  fetchRewards,
  fetchSiteConfig,
  calculateRewardsStats,
} from '@/services/api/affiliateService';
import styles from './AffiliateDashboard.module.css';
import homeStyles from '../../Home/Home.module.css';

/**
 * AffiliateDashboard Component
 * Optimized with React Query for fast data fetching and caching
 */
const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [token] = useState(storage.getKey('token') || null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);

  // Fetch site config (cached for 10 minutes - rarely changes)
  const {
    data: config = {
      registerbonus: 0,
      affiliatebonus: 0,
      comissionpercent: 0,
      sports: false,
    },
    isLoading: isLoadingConfig,
  } = useQuery({
    queryKey: ['siteConfig'],
    queryFn: fetchSiteConfig,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 15 * 60 * 1000, // Keep in cache for 15 minutes
  });

  // Fetch referral info (cached for 5 minutes) - only if logged in
  const {
    data: referralInfo,
    isLoading: isLoadingReferral,
  } = useQuery({
    queryKey: ['affiliate', 'referralInfo'],
    queryFn: fetchReferralInfo,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1, // Reduced retries
    enabled: !!token, // Only fetch if logged in
    refetchOnWindowFocus: false, // Prevent refetch on focus
    refetchOnMount: false, // Prevent refetch on mount if data exists
    refetchOnReconnect: false, // Prevent refetch on reconnect
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Fetch team members (only if referral code exists)
  const {
    data: teamData,
    isLoading: isLoadingTeam,
  } = useQuery({
    queryKey: ['affiliate', 'team', referralInfo?.referralCode],
    queryFn: () => fetchTeamMembers(referralInfo.referralCode),
    enabled: !!referralInfo?.referralCode && !!token,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000,
  });

  // Fetch rewards (only if referral code exists)
  const {
    data: rewardsData,
    isLoading: isLoadingRewards,
  } = useQuery({
    queryKey: ['affiliate', 'rewards', referralInfo?.referralCode],
    queryFn: () => fetchRewards(referralInfo.referralCode),
    enabled: !!referralInfo?.referralCode && !!token,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    gcTime: 10 * 60 * 1000,
  });

  // Calculate stats with memoization
  const stats = useMemo(() => {
    if (!rewardsData || !teamData) {
      return {
        totalMembers: 0,
        totalRewards: 0,
        referralRewards: 0,
        commissionRewards: 0,
      };
    }

    return calculateRewardsStats(
      rewardsData.rewards,
      teamData.teamMembers?.length || 0,
      rewardsData.totalRewards || 0
    );
  }, [rewardsData, teamData]);

  // Memoize live rewards data
  const liveRewardsData = useMemo(() => {
    const getRandomAmount = () => {
      const min = 0.15;
      const max = 60.0;
      return `$${(Math.random() * (max - min) + min).toFixed(2)}`;
    };

    const names = [
      'Antony Roberts', 'Aarav Verma', 'সিয়া রাওয়াত', 'Sofia Hernández', 'Vivaan Desai',
      'Li Wei', 'Siya Rawat', 'محمد علي', 'Daisuke Tanaka', 'Emma Johnson',
      'Carlos Romero', '张伟', 'Meera Iyer', 'Riya Sharma', 'Johannes Müller',
      'Aditi Patel', 'Jithin Kumar', 'Olivia Smith', 'Mohammed Ali', 'Tariq Abdullah',
      'Miguel Oliveira', 'Yuki Nakajima', 'Nina Popov', 'Lucía Martín', 'Ayaan Sheikh',
      'Marie Dubois', 'ソフィア・ヘルナンデス', '李伟', '田中大輔', 'मीरा अय्यर',
      'अदिति पटेल', 'ゆき なかじま', 'Нина Попов', 'আয়ান শেখ', 'चरणजीत सिंह',
      'Ananya Chakraborty', '한미영', 'Владимир Иванов', 'İsmail Yıldırım', 'Αλέξανδρος Δημητρίου',
      'Elena Rossi', 'Fatima Zahra', 'Joaquín Morales', 'Chantal Dupont', 'Sara Mehta',
    ];

    return names.map((name) => ({ name, amount: getRandomAmount() }));
  }, []);

  const copyToClipboard = useCallback((text, type) => {
    if (!text || text === 'Please login to see your link.' || text === 'Please login to see your code.') {
      return;
    }

    navigator.clipboard
      .writeText(text)
      .then(() => {
        if (type === 'link') {
          setCopiedLink(true);
          setTimeout(() => setCopiedLink(false), 2000);
        } else {
          setCopiedCode(true);
          setTimeout(() => setCopiedCode(false), 2000);
        }
      })
      .catch((err) => {
        console.error('Failed to copy text: ', err);
      });
  }, []);

  // Combined loading state
  const isInitialLoading = isLoadingConfig || isLoadingReferral || 
    (referralInfo?.referralCode && (isLoadingTeam || isLoadingRewards));

  // Show loader while initial data is loading
  if (isInitialLoading) {
    return (
      <div className={styles.wrapper}>
        <div className={homeStyles.loadingContainer}>
          <div className={homeStyles.loader}>
            <span className={homeStyles.loaderText}>loading</span>
            <span className={homeStyles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  const logged = !!token;
  const referralCode = referralInfo?.referralCode || '';
  const referralLink = referralInfo?.referralLink || '';
  const teamMembers = teamData?.teamMembers || [];

  return (
    <div className={styles.wrapper}>
      <div className={styles.dashboardSection}>
        <div className={styles.bodyWrapper} style={{ width: isMobile ? '100%' : '70%' }}>
          <div className={styles.sectionHeader}>
            <h1 className={styles.sectionTitle}>Invite a Friend to Get</h1>
          </div>

          <div className={styles.sectionContentBody}>
            <div className={styles.rewardsInfo}>
              <div className={styles.rewardItem}>
                <div className={styles.rewardValue}>{config.affiliatebonus} JKP</div>
                <div className={styles.rewardLabel}>Referral Rewards</div>
              </div>
              <span className={styles.divider}>|</span>
              <div className={styles.rewardItem}>
                <div className={styles.rewardValue}>{config.comissionpercent}%</div>
                <div className={styles.rewardLabel}>Commission Rewards</div>
              </div>
            </div>

            <div className={styles.description}>
              Get {config.affiliatebonus} JKP for each friend you invite, plus up to {config.comissionpercent}%
              commission on their wager. Enjoy consistent commission, whether they win or lose, in our Casino and
              Sportsbook. Start earning now!
            </div>

            <div className={styles.inputsWrapper}>
              <div className={styles.inputGroup} style={{ width: isMobile ? '100%' : 'max-content' }}>
                <label className={styles.inputLabel}>Referral Link</label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    value={logged ? referralLink : 'Please login to see your link.'}
                    readOnly
                    className={styles.input}
                  />
                  <button
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(referralLink, 'link')}
                    disabled={!logged || !referralLink}
                  >
                    {copiedLink ? '✓' : '📋'}
                  </button>
                </div>
              </div>

              <div className={styles.inputGroup} style={{ width: isMobile ? '100%' : 'max-content' }}>
                <label className={styles.inputLabel}>Referral Code</label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    value={logged ? referralCode : 'Please login to see your code.'}
                    readOnly
                    className={styles.input}
                  />
                  <button
                    className={styles.copyButton}
                    onClick={() => copyToClipboard(referralCode, 'code')}
                    disabled={!logged || !referralCode}
                  >
                    {copiedCode ? '✓' : '📋'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.statsContainer}>
          <div className={styles.statsWrapper}>
            <div className={styles.statsSection}>
              <div className={styles.statItem}>
                <img
                  src="/assets/images/bcgames/affiliate/ad1.png"
                  alt="Trophy Icon"
                  className={styles.statIcon}
                />
                <div className={styles.statTitle}>Total Rewards</div>
                <div className={styles.statValue}>{stats.totalRewards.toFixed(2)} FC</div>
              </div>

              <div className={styles.statItem}>
                <img
                  src="/assets/images/bcgames/affiliate/ad2.png"
                  alt="Persons Icon"
                  className={styles.statIcon}
                />
                <div className={styles.statTitle}>Total Friends</div>
                <div className={styles.statValue}>{stats.totalMembers}</div>
              </div>
            </div>

            <div className={styles.rewardsSection}>
              <div className={styles.rewardCard}>
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAANlBMVEUAAAAl7ook7ook74kk74oo74cj7Ygl7ogg74Al7okk7Ykk7Ygm8okk7ook74kj7Ikg748k7olWkP/+AAAAEXRSTlMAn7+vQCCQYBDfj4Bfz39QEOuVG+cAAACQSURBVCjPrVFJDsQgDMteaDuL///ZQQg1ohzmUl8SY4FNQs+i7OzJnLmMlgFtJVlkKykIwNdlwUm2i7DRBtF8uFFFh2KjCQpocRluCQNgRJ9eOtIfB9EbV6SMAmj3kUWI4sciBFCp4QXw3VyHl93jVmt6zbjLB888dRVsZCwSfSTs/4YYUxRN9uV5UVHoUfwAMFkG5VKTGL0AAAAASUVORK5CYII="
                  alt="Trophy Icon"
                  className={styles.rewardIcon}
                />
                <div className={styles.rewardCardTitle}>Referral Rewards</div>
                <div className={styles.rewardCardValue}>{stats.referralRewards.toFixed(2)} $</div>
              </div>

              <div className={styles.rewardCard}>
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAMAAADXqc3KAAAAOVBMVEUAAAC0v8K0vsGzvsKyvsGzv8Kvv7+vv7+3v7+zvsGzvsG0vcKzvsGzvsK0v7+yvsCzv7+1v7+zvsGFcux/AAAAEnRSTlMAn9+/YH8QICCQ73DPsHBQQDC7HTtpAAAAkUlEQVQoz7WPSw7FIAhFBbFV+7/7X+wD2zRBp69noObcgBC+Ia2oUQY9rWikPiCzpOfi/QxwQo5A9cGiQaEZSvY/mzqpnX0FeOZdr8sPpUakWsfgKQAxLA5jYuzDgkG2lcvV2Whr5Ps9vVrOAw1msbnpXZoED4d6FB9sOefJPIWugpnr7cdWzutI+xEfthT+zg/u+gnq0ekBWwAAAABJRU5ErkJggg=="
                  alt="Persons Icon"
                  className={styles.rewardIcon}
                />
                <div className={styles.rewardCardTitle}>Commission Rewards</div>
                <div className={styles.rewardCardValue}>{stats.commissionRewards.toFixed(2)} FC</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.bodyWrapper}>
        <div className={styles.sectionContentBody} style={{ minHeight: '50vh' }}>
          <h2 className={styles.friendsTitle}>My Friends</h2>
          {teamMembers.length > 0 ? (
            <div className={styles.friendsList}>
              {teamMembers.map((member, index) => (
                <div key={index} className={styles.friendItem}>
                  {member.name || `Friend ${index + 1}`}
                </div>
              ))}
            </div>
          ) : (
            <p className={styles.noMembers}>No friends yet. Start inviting!</p>
          )}
        </div>
      </div>

      <div className={styles.bodyWrapper} style={{ marginTop: '20px' }}>
        <div className={styles.sectionContentBody}>
          <div className={styles.sectionHeader}>
            <div className={styles.indicator}></div>
            <h2 className={styles.sectionTitle}>Live Rewards</h2>
          </div>

          <div className={styles.liveRewardsContainer}>
            <div className={styles.totalRewardBox}>
              <div className={styles.totalRewardLabel}>Total Reward Sent To-Date</div>
              <div className={styles.totalRewardAmount}>250650.08 FC</div>
            </div>

            <div className={styles.scrollingWrapper}>
              <div className={styles.scrollingContent}>
                {liveRewardsData.concat(liveRewardsData).map((item, index) => (
                  <div key={index} className={styles.rewardItem}>
                    {item.name} <span className={styles.rewardAmount}>{item.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateDashboard;
